import styled from 'foundations-theming/utils';
import { breakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';

/* ----- Desktop grid layout ----- */

export const ComparisonGrid = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__ComparisonGrid"
})(({
  theme
}) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  columnGap: theme.space['300'],
  marginBlockStart: theme.space['1000'],
  [breakpoint.tablet]: {
    display: 'none'
  }
}));
export const HubSpotCardBg = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__HubSpotCardBg"
})(({
  isHighlighted,
  theme
}) => ({
  gridColumn: 1,
  gridRow: '1 / 11',
  backgroundColor: '#ffffff',
  border: isHighlighted ? `2px solid ${theme.color['fill-accent-orange-default']}` : '1px solid #CBD6E2',
  borderRadius: '8px',
  boxShadow: '0 1px 4px 0 rgba(0,0,0,0.08)',
  zIndex: 0
}));
export const StripeCardBg = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__StripeCardBg"
})(() => ({
  gridColumn: 2,
  gridRow: '1 / 11',
  backgroundColor: '#ffffff',
  border: '1px solid #CBD6E2',
  borderRadius: '8px',
  boxShadow: '0 1px 4px 0 rgba(0,0,0,0.08)',
  zIndex: 0
}));
export const GridCell = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__GridCell"
})(({
  row,
  col
}) => ({
  gridRowStart: row,
  gridColumnStart: col
}));
export const ContentCell = styled(GridCell)(({
  theme
}) => ({
  padding: `${theme.space['400']} ${theme.space['600']}`,
  zIndex: 1
}));
export const DividerCell = styled(GridCell)(() => ({
  borderTop: '1px solid #E5E8EB',
  margin: '0 24px',
  zIndex: 1
}));

/* HubSpot header row — margin inset keeps orange border visible when highlighted */
export const HubSpotIntroCell = styled(GridCell)(({
  isHighlighted,
  theme
}) => ({
  zIndex: 1,
  margin: '2px 2px 0',
  backgroundColor: isHighlighted ? '#FFF3EE' : '#ffffff',
  borderRadius: '6px 6px 0 0',
  padding: `${theme.space['400']} ${theme.space['600']}`,
  borderBottom: '1px solid #E5E8EB'
}));

/* Stripe header row — mirrors the dividing line under HubSpot's intro cell */
export const StripeIntroCell = styled(GridCell)(({
  theme
}) => ({
  padding: `${theme.space['400']} ${theme.space['600']}`,
  zIndex: 1,
  borderBottom: '1px solid #E5E8EB'
}));

/* Mobile fallback — stacked self-contained cards, hidden on desktop */
export const MobileCardsLayout = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__MobileCardsLayout"
})(({
  theme
}) => ({
  display: 'none',
  flexDirection: 'column',
  gap: theme.space['600'],
  marginBlockStart: theme.space['1000'],
  [breakpoint.tablet]: {
    display: 'flex'
  }
}));

/* ----- Mobile card layout primitives ----- */

export const CardShell = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__CardShell"
})(({
  isHighlighted,
  connectedAbove,
  theme
}) => Object.assign({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: connectedAbove ? '0 0 8px 8px' : '8px',
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  border: isHighlighted ? `2px solid ${theme.color['fill-accent-orange-default']}` : '1px solid #CBD6E2'
}, connectedAbove && {
  borderTop: 'none'
}, {
  boxShadow: '0 1px 4px 0 rgba(0,0,0,0.08)',
  [breakpoint.tablet]: {
    width: '100%'
  }
}));
export const CardBody = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__CardBody"
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  padding: theme.space['600'],
  gap: theme.space['300']
}));
export const MobileCardHeader = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__MobileCardHeader"
})(({
  isHighlighted,
  theme
}) => ({
  backgroundColor: isHighlighted ? '#FFF3EE' : '#ffffff',
  padding: theme.space['600'],
  borderRadius: '6px 6px 0 0'
}));

/* ----- Shared card content primitives ----- */

export const SectionDivider = styled.hr.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__SectionDivider"
})(() => ({
  border: 'none',
  borderBlockStart: '1px solid #E5E8EB',
  marginBlock: '16px',
  marginInline: 0
}));
export const FeeRowRoot = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__FeeRowRoot"
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['50'],
  marginBlockStart: theme.space['400']
}));
export const FeeValueLine = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__FeeValueLine"
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.space['200']
}));
export const ComparisonText = styled.span.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__ComparisonText"
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: theme.color['fill-positive-default'],
  fontSize: '14px',
  fontWeight: 600,
  marginBlockStart: theme.space['50']
}));
export const BadgeRoot = styled.span.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__BadgeRoot"
})(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.space['100'],
  padding: `4px ${theme.space['200']}`,
  border: '1px solid #CBD6E2',
  borderRadius: '20px',
  backgroundColor: '#F5F8FA',
  whiteSpace: 'nowrap'
}));
export const PaymentMethodsGrid = styled.div.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__PaymentMethodsGrid"
})(({
  theme
}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.space['200'],
  marginBlockStart: theme.space['300']
}));

/* ----- HubSpot card header ----- */

export const HubSpotHeaderTitle = styled.span.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__HubSpotHeaderTitle"
})(() => ({
  fontSize: '20px',
  fontWeight: 500,
  lineHeight: 1.3
}));
export const RecommendedBadge = styled.span.withConfig({
  displayName: "NewPublicPaymentOptionsSectionStyles__RecommendedBadge"
})(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: `2px 8px`,
  border: 'none',
  borderRadius: '100px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: '#2D3E50',
  color: '#ffffff',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap'
}));